import { UseFormReturn } from "react-hook-form";
import { DrawerClose, DrawerHeader } from "@/components/ui/drawer";
import { FormField } from "@/components/ui/form";
import { Step } from "./constants";
import { cn } from "@/lib/utils";
import { CaretRight } from "@phosphor-icons/react";
import { Button, buttonVariants } from "@/components/ui/button";
import { formatDate } from "date-fns";
import FilePreview from "@/components/custom/file-preview";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import ScrollableCalendar from "@/components/custom/scrollable-calendar";
import { ExecutionEntryFormValues } from "./index";
import { MaintenanceTask } from "@/lib/types";
import ManageAssigneesDrawer from "../manage-assignees-drawer";

const InitialStep = ({
  form,
  setStep,
  title,
  requiresRunningHours = true,
  task,
  vesselId,
  onTaskUpdated,
  isSubmitting,
}: {
  form: UseFormReturn<ExecutionEntryFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  title?: string;
  requiresRunningHours?: boolean;
  task?: MaintenanceTask;
  vesselId?: string;
  onTaskUpdated?: () => void;
  /** Disables Save while a submission is in flight to prevent duplicate entries. */
  isSubmitting?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DrawerHeader
        title={title ?? t("title.add_execution_entry")}
        leftButton={
          <DrawerClose asChild>
            <button
              type="button"
              className="text-[15px] font-medium leading-5 text-[#45D3EC]"
            >
              {t("button.cancel")}
            </button>
          </DrawerClose>
        }
        rightButton={
          <button
            data-testid="execution-entry-form-submit-button"
            type="submit"
            disabled={isSubmitting}
            className="text-[15px] font-medium leading-5 text-[#45D3EC] disabled:opacity-50"
          >
            {t("button.save")}
          </button>
        }
      />

      <div className="mt-[30px] w-full space-y-[15px]">
        <ExecutionDateButton form={form} />
        {requiresRunningHours && (
          <RunningHoursButton form={form} setStep={setStep} />
        )}
        {task && vesselId && onTaskUpdated && (
          <AssigneesButton
            task={task}
            vesselId={vesselId}
            onTaskUpdated={onTaskUpdated}
          />
        )}
        <UploadFilesButton form={form} />
        <NoteButton form={form} setStep={setStep} />
      </div>
    </>
  );
};

export default InitialStep;

const ExecutionDateButton = ({
  form,
}: {
  form: UseFormReturn<ExecutionEntryFormValues>;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full">
      <div
        data-invalid={!!form.formState.errors.executionDate}
        data-testid="execution-entry-form-execution-date-button"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          form.getValues("executionDate")
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center !justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.executionDate,
          }
        )}
      >
        {form.getValues("executionDate") ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.execution_date")}
            </span>
            <span className="text-start text-[15px] text-white">
              {formatDate(
                form.getValues("executionDate")!,
                "dd MMMM yyyy - HH:mm"
              )}
            </span>
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.execution_date")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </div>

      {isOpen && (
        <ScrollableCalendar
          data-testid="execution-entry-form-execution-date-calendar"
          date={
            form.getValues("executionDate")
              ? new Date(form.getValues("executionDate")!)
              : new Date()
          }
          setDate={(date) => {
            form.setValue("executionDate", date);
            form.trigger("executionDate");
          }}
        />
      )}
    </div>
  );
};

const RunningHoursButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<ExecutionEntryFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  const runningHours = form.watch("runningHoursAtExecution");

  return (
    <>
      {runningHours !== undefined && runningHours !== null ? (
        <button
          data-testid="execution-entry-form-running-hours-button"
          type="button"
          onClick={() => setStep(Step.RUNNING_HOURS)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.running_hours")}
          </span>
          <p className="text-start text-[15px] text-white">
            {runningHours} {t("label.running_hours").toLowerCase()}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.runningHoursAtExecution}
          data-testid="execution-entry-form-running-hours-button"
          onClick={() => setStep(Step.RUNNING_HOURS)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.runningHoursAtExecution,
          })}
        >
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.running_hours")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        </Button>
      )}
    </>
  );
};

const UploadFilesButton = ({
  form,
}: {
  form: UseFormReturn<ExecutionEntryFormValues>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <label
        data-invalid={!!form.formState.errors.files}
        data-testid="execution-entry-form-files-label"
        htmlFor="execution-entry-files"
        className={cn(
          form.getValues("files") && form.getValues("files").length > 0
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.files,
          }
        )}
      >
        {form.getValues("files").length > 0 ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.attachments")}
            </span>
            <FilePreview
              className="mt-1"
              files={
                "url" in form.watch("files")[0]
                  ? undefined
                  : (form.watch("files") as unknown as File[])
              }
              urls={
                "url" in form.watch("files")[0]
                  ? (form.watch("files") as { id: string; url: string }[]).map(
                      (file) => file.url
                    )
                  : undefined
              }
              onRemove={(file) => {
                if (typeof file === "string") {
                  form.setValue(
                    "files",
                    (
                      form.watch("files") as { id: string; url: string }[]
                    ).filter((f) => f.url !== file)
                  );
                } else {
                  form.setValue(
                    "files",
                    (form.watch("files") as unknown as File[]).filter(
                      (f) => f !== file
                    ) as unknown as { id: string; url: string }[]
                  );
                }
              }}
            />
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.attachments")}
            <span className="text-base text-white/50">
              ({t("label.optional")})
            </span>
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </label>
      <FormField
        control={form.control}
        name="files"
        render={({ field: { value: _, onChange, ...rest } }) => (
          <input
            data-testid="execution-entry-form-files-input"
            id="execution-entry-files"
            type="file"
            multiple
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              form.watch("files");
              onChange(Array.from(e.target.files || []));
            }}
            {...rest}
          />
        )}
      />
    </>
  );
};

const AssigneesButton = ({
  task,
  vesselId,
  onTaskUpdated,
}: {
  task: MaintenanceTask;
  vesselId: string;
  onTaskUpdated: () => void;
}) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const assignees = task.taskDetail.assignees ?? [];

  return (
    <>
      {assignees.length > 0 ? (
        <button
          data-testid="execution-entry-form-assignees-button"
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.assignees")}
          </span>
          <p className="line-clamp-1 text-start text-[15px] text-white">
            {assignees.map((a) => a.fullName).join(", ")}
          </p>
        </button>
      ) : (
        <Button
          data-testid="execution-entry-form-assignees-button"
          onClick={() => setDrawerOpen(true)}
          type="button"
          size="lg"
          variant="outline"
          className="w-full"
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.assignees")}
            <span className="text-base text-white/50">
              ({t("label.optional")})
            </span>
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}

      <ManageAssigneesDrawer
        open={drawerOpen}
        setOpen={setDrawerOpen}
        task={task}
        vesselId={vesselId}
        onUpdated={onTaskUpdated}
      />
    </>
  );
};

const NoteButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<ExecutionEntryFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("notes") && form.getValues("notes")!.trim() !== "" ? (
        <button
          data-testid="execution-entry-form-note-button"
          type="button"
          onClick={() => setStep(Step.NOTES)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.notes")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("notes")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.notes}
          data-testid="execution-entry-form-note-button"
          onClick={() => setStep(Step.NOTES)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.notes,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_note")}
            <span className="text-base text-white/50">
              ({t("label.optional")})
            </span>
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};
